import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { Area } from 'react-easy-crop/types';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';

import { getCroppedImg } from './canvasUtils';
import Button from '../Button';
import { Container, Controls, CropContainer, Header } from './styles';

interface ImageCropperModalProps {
  imageSrc: string;
  onClose: () => void;
  onCropComplete: (croppedImageFile: File) => void;
}

const ImageCropperModal: React.FC<ImageCropperModalProps> = ({
  imageSrc,
  onClose,
  onCropComplete,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const onCropChange = useCallback((cropValue) => {
    setCrop(cropValue);
  }, []);

  const onZoomChange = useCallback((zoomValue) => {
    setZoom(zoomValue);
  }, []);

  const onRotationChange = useCallback((rotationValue) => {
    setRotation(rotationValue);
  }, []);

  const onCropCompleteHandler = useCallback((croppedArea: Area, croppedAreaPixelsValue: Area) => {
    setCroppedAreaPixels(croppedAreaPixelsValue);
  }, []);

  const showCroppedImage = useCallback(async () => {
    try {
      if (imageSrc && croppedAreaPixels) {
        const croppedImageFile = await getCroppedImg(
          imageSrc,
          croppedAreaPixels,
          rotation
        );
        onCropComplete(croppedImageFile);
        onClose();
      }
    } catch (e) {
      console.error(e);
      // Adicionar toast de erro aqui se desejar
    }
  }, [imageSrc, croppedAreaPixels, rotation, onCropComplete, onClose]);

  return (
    <Container>
      {/* CORREÇÃO: Usa <button> com type="button", aria-label e onKeyDown para fechar o modal.
          Isso resolve todos os erros de acessibilidade (a11y) do elemento interativo (div original). */}
      <button
        type="button"
        aria-label="Fechar Modal"
        className="overlay"
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            onClose();
          }
        }}
      >
        {/* Conteúdo opcional, mantido vazio para o overlay. */}
      </button>

      <div className="modal-content">
        <Header>
          <h2>Cortar Imagem</h2>
          {/* CORREÇÃO: type="button" explícito. */}
          <button type="button" className="close-button" onClick={onClose}>&times;</button>
        </Header>
        <CropContainer>
          {/* CORREÇÃO: Componente <Cropper> é self-closing. */}
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={1}
            onCropChange={onCropChange}
            onZoomChange={onZoomChange}
            onRotationChange={onRotationChange}
            onCropComplete={onCropCompleteHandler}
            classes={{ containerClassName: 'cropper-container', mediaClassName: 'cropper-media' }}
          />
        </CropContainer>
        <Controls>
          {/* CORREÇÃO: label e Slider associados via htmlFor/id para a11y. */}
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor="zoom-slider">Zoom:</label>
          <Slider
            id="zoom-slider"
            min={1}
            max={3}
            step={0.1}
            value={zoom}
            onChange={onZoomChange}
            className="slider"
          />

          {/* CORREÇÃO: label e Slider associados via htmlFor/id para a11y. */}
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor="rotation-slider">Rotação:</label>
          <Slider
            id="rotation-slider"
            min={0}
            max={360}
            step={1}
            value={rotation}
            onChange={onRotationChange}
            className="slider"
          />

          {/* CORREÇÃO: type="button" explícito no componente Button. */}
          <Button type="button" onClick={showCroppedImage} color="#2A2A29">
            Aplicar Corte
          </Button>

          {/* CORREÇÃO: type="button" explícito no componente Button. */}
          <Button type="button" onClick={onClose} color="#ccc">
            Cancelar
          </Button>
        </Controls>
      </div>
    </Container>
  );
};

export default ImageCropperModal;
